import {Button} from '@mantine/core';
import {Space} from 'src/design/atoms/Space';
import {
  IconDownload,
  IconFileDescription,
  IconFolder,
} from '@tabler/icons-react';
import React, {useState} from 'react';
import {useParams} from 'react-router-dom';
import type {
  DocumentFolderResponse,
  DocumentResponse,
} from 'src/api/ProactApiClient';
import {ModuleEnum} from 'src/api/ProactApiClient';
import {BackBar} from 'src/app/layout/BackBar';
import {DocumentTreeModal} from 'src/pages/Modules/DocumentManagement/shared/DocumentTreeModal';
import {DataList} from 'src/design/organisms/DataList';
import {ErrorAlert} from 'src/design/molecules/ErrorAlert';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SidebarLinkDocumentManager} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {toDocumentListFilter} from 'src/app/documents/documentTreeBrowserState';
import {useAppSelector} from 'src/redux/hooks';
import {
  useDeleteDocumentMutation,
  useGetDocumentListQuery,
  useMoveDocumentMutation,
} from 'src/redux/queries/document';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import useDebounce from 'src/shared/hooks/useDebounce';
import {useDocumentAncestors} from 'src/shared/hooks/useDocumentAncestors';
import {useDocumentManagementBreadcrumbs} from 'src/shared/hooks/useDocumentManagementBreadcrumbs';
import {useDocumentReviewsFeature} from 'src/shared/hooks/useDocumentReviewsFeature';
import {getUrlWithEncodingAndAuth} from 'src/shared/utils/urlHelpers';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {DocumentListMeta} from '../../documentHelpers';
import {DocumentReviewPillMapping} from '../../documentReviewHelpers';
import {useDocumentListUrlState} from './useDocumentListUrlState';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const maxPageRows = 10;

/**
 * Folder/file browser for Document Management — the original `DocumentList`
 * page content, extracted so it can be embedded as one of several tabs when
 * the `DocumentReviews` feature is active.
 */
export const CurrentDocumentListView: React.FC = () => {
  const {nodeId} = useParams();
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();

  const perms = useModulePermissions(ModuleEnum.Documents);
  const hasReviewsFeature = useDocumentReviewsFeature();

  const {
    search: searchTerm,
    setSearch: setSearchTerm,
    currentPage,
    setCurrentPage,
  } = useDocumentListUrlState();
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const showFullActionBar = !!(nodeId && nodeId !== '0');
  const [showMoveModal, setShowMoveModal] = useState<boolean>(false);

  const {accessToken} = useAppSelector(state => state.auth);

  const {
    currentData: listResponse,
    isLoading,
    isFetching,
    isError,
    error: listError,
  } = useGetDocumentListQuery(
    {
      start: (currentPage - 1) * maxPageRows,
      length: maxPageRows,
      nodeId: toDocumentListFilter(nodeId).nodeId,
      search: debouncedSearchTerm,
    },
    // Review status (Pending/Approved/Rejected) flips via an async background job
    // after an approval/rejection, so the cached list can be stale on return.
    // Refetch on mount/arg change to surface the current status.
    {refetchOnMountOrArgChange: true}
  );

  const node: DocumentFolderResponse | undefined = listResponse;
  const indexListData: DocumentResponse[] | undefined =
    listResponse?.response?.data;
  const totalRecords = listResponse?.response?.totalRecords ?? 0;

  const [deleteDocument] = useDeleteDocumentMutation();
  const [moveDocument] = useMoveDocumentMutation();

  const ancestors = useDocumentAncestors(
    showFullActionBar ? nodeId : undefined
  );

  const {breadcrumbs, fallbackTo} = useDocumentManagementBreadcrumbs({
    mode: showFullActionBar ? 'folder-list' : 'list',
    nodeId: nodeId,
    parentId: node?.parentId,
    nodeName: node?.name,
    ancestors,
  });
  const {goBack} = useBackNavigation(fallbackTo);

  const handleNavigate = (id: string | number, doc: DocumentResponse) => {
    if (doc.isFolder) {
      navigate(PathUtility.DocumentsManagerList(id));
    } else {
      navigate(PathUtility.DocumentManagerViewFile(id, 'content'));
    }
  };

  const getRowIcon = (item: DocumentResponse) => {
    return item.isFolder ? <IconFolder /> : <IconFileDescription />;
  };

  const handlePageChange = (e: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleDelete = async () => {
    try {
      await deleteDocument(+nodeId!).unwrap();
      setAlert(`${node?.name} was successfully deleted.`, 'success');
      goBack();
    } catch {
      setAlert(
        'An error occurred deleting folder.  Please make sure the folder is empty and try again.',
        'error'
      );
    }
  };

  const handleAddNewFolder = () => {
    navigate(
      PathUtility.DocumentManagerCreateFolder(
        toDocumentListFilter(nodeId).nodeId ?? 0
      )
    );
  };

  const handleAddNewFile = () => {
    navigate(
      PathUtility.DocumentManagerCreateFile(
        toDocumentListFilter(nodeId).nodeId ?? 0
      )
    );
  };

  const handleEdit = () => {
    navigate(PathUtility.DocumentManagerEditFolder(node?.id as number));
  };

  const handleMove = () => {
    setShowMoveModal(true);
  };

  const handleMoveConfirm = async (newParentId: number | null) => {
    try {
      if (!node?.id || !newParentId) {
        setAlert('Document or parent folder not found.', 'error');
        return;
      }

      await moveDocument({
        id: node.id,
        newParentId: newParentId,
      }).unwrap();

      setAlert(`${node?.name} was successfully moved.`, 'success');
    } catch {
      setAlert('An error occurred moving the item.', 'error');
    }

    setShowMoveModal(false);
  };

  const handleDownload = (attachment: string) => {
    const url = JSON.parse(attachment)[0].value;
    window.open(getUrlWithEncodingAndAuth(url, accessToken), '_blank');
  };

  const renderSubtext = () => {
    return '';
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  return (
    <>
      {showFullActionBar ? (
        <BackBar fallbackTo={fallbackTo} breadcrumbs={breadcrumbs} />
      ) : (
        <BackBar hideBackButton breadcrumbs={breadcrumbs} />
      )}
      <PageHeader>
        <PageHeader.Title icon={SidebarLinkDocumentManager.icon}>
          {showFullActionBar ? node?.name : 'All Documents'}
        </PageHeader.Title>
        <PageHeader.Search
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search..."
        />
        <PageHeader.Actions>
          {perms.create && (
            <>
              <PageHeader.Action variant="create" onClick={handleAddNewFolder}>
                Add Folder
              </PageHeader.Action>
              <PageHeader.Action variant="create" onClick={handleAddNewFile}>
                Add File
              </PageHeader.Action>
            </>
          )}
          {showFullActionBar && node && !isError && perms.update && (
            <>
              <PageHeader.Action variant="edit" onClick={handleEdit}>
                Edit
              </PageHeader.Action>
              <PageHeader.Action onClick={handleMove}>Move</PageHeader.Action>
            </>
          )}
          {showFullActionBar && node && !isError && perms.delete && (
            <PageHeader.Action variant="delete" onClick={handleDelete}>
              Delete
            </PageHeader.Action>
          )}
        </PageHeader.Actions>
      </PageHeader>

      <Space mb="md" />

      {isError ? (
        <ErrorAlert message={parseErrorMessage(listError)} mt={0} />
      ) : (
        <DataList
          isLoading={isLoading || isFetching}
          data={indexListData}
          meta={DocumentListMeta}
          onRowClick={handleNavigate}
          onGetRowIcon={getRowIcon}
          onGetPillMapping={
            hasReviewsFeature ? DocumentReviewPillMapping : undefined
          }
          currentPage={currentPage}
          totalRecords={totalRecords}
          recordsPerPage={maxPageRows}
          onPageChange={handlePageChange}
          onRowSubTextRender={renderSubtext}
          hasCreationLogic={false}
          noDataText="No Files/Folders"
          onRenderAfterRowNode={(item: DocumentResponse) =>
            item.attachmentURL &&
            perms.view && (
              <Button
                leftSection={<IconDownload />}
                size="sm"
                onClick={() =>
                  item.attachmentURL && handleDownload(item.attachmentURL)
                }>
                View
              </Button>
            )
          }
        />
      )}

      <DocumentTreeModal
        opened={showMoveModal}
        onClose={() => setShowMoveModal(false)}
        onConfirm={handleMoveConfirm}
        title="Move Folder"
        currentItemName={node?.name}
        currentItemType="folder"
        currentItemId={node?.id}
      />
    </>
  );
};
